import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { CustomDateInputComponent } from '../../../../shared/components/custom-date-input/custom-date-input.component';
import { CustomDropdownComponent } from '../../../../shared/components/custom-dropdown/custom-dropdown.component';
import { combineLatest, firstValueFrom } from 'rxjs';
import { RouterLink } from '@angular/router';
import { LocationService } from '../../../../core/api/location.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '../../../../core/interfaces/location';
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { CommonValidatorService } from '../../../../core/services/validators/common-validator.service';
import { HistoricalDataElectricity, HistoricalDataGas } from '../../../../core/interfaces/invoice-interfaces';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { CustomNumberInputComponent } from '../../../../shared/components/custom-number-input/custom-number-input.component';

@Component({
  selector: 'app-historical-data-input-dialog',
  standalone: true,
  imports: [FormsModule, DialogModule, TranslocoPipe, CommonModule, CustomButtonComponent, CustomDateInputComponent, CustomNumberInputComponent, CustomDropdownComponent, RouterLink],
  templateUrl: './historical-data-input-dialog.component.html',
  styleUrl: './historical-data-input-dialog.component.css'
})
export class HistoricalDataInputDialogComponent implements OnInit {
  constructor(private translocoService: TranslocoService, private locationService: LocationService, private dateConversion: DateConversionService,
    private commonValidator: CommonValidatorService, private apiError: ApiErrorService, private invoiceService: InvoiceService) { }
  visible: boolean = false;

  forPeriodDate: Date | undefined;
  forPeriodInputError: string | null = null;

  selectedLocationDropdown: number | undefined;
  locationsDropdownOptions: string[] = [];
  saveClicked: boolean = false;

  energyTypeDropdownOptions: string[] = ['Electricity', 'Gas'];
  selectedEnergyTypeDropdown: number | undefined;


  // Inputs and errors for all fields
  totalConsumptionInput = '';
  totalConsumptionInputError: string | null = null;

  invoiceAmountInput = '';
  invoiceAmountInputError: string | null = null;

  totalTaxesInput = '';
  totalTaxesInputError: string | null = null;

  monthConsumptionF1Input = '';
  monthConsumptionF1InputError: string | null = null;

  monthConsumptionF2Input = '';
  monthConsumptionF2InputError: string | null = null;

  monthConsumptionF3Input = '';
  monthConsumptionF3InputError: string | null = null;

  energyChargePriceInput = '';
  energyChargePriceInputError: string | null = null;

  reactiveEnergyCostsInput = '';
  reactiveEnergyCostsInputError: string | null = null;

  tmsChargesInput = '';
  tmsChargesInputError: string | null = null;

  dispatchCostsInput = '';
  dispatchCostsInputError: string | null = null;

  fixedQuotaInput = '';
  fixedQuotaInputError: string | null = null;

  spreadInput = '';
  spreadInputError: string | null = null;


  // Dialog error
  dialogErrorMessage: string | null = null;

  // Button click
  confirmClicked: boolean = false;

  // Locations
  listOfLocations: Location[] = [];

  ngOnInit(): void {
    combineLatest([
      this.translocoService.selectTranslate('shared.electricity'),
      this.translocoService.selectTranslate('shared.gas'),
    ]).subscribe(([electricityTranslation, gasTranslation]) => {
      this.energyTypeDropdownOptions = [
        electricityTranslation,
        gasTranslation
      ];
    });
  }

  openModal(editLocation?: Location): void {
    this.visible = true;

    this.resetInvoiceDialogData();
  }

  closeModal(): void {
    this.visible = false;
  }

  resetInvoiceDialogData(): void {
    this.forPeriodDate = undefined;
    this.forPeriodInputError = null;

    this.loadLocationsData();

    this.selectedLocationDropdown = undefined;
    this.selectedEnergyTypeDropdown = undefined;

    this.totalConsumptionInput = this.invoiceAmountInput = this.totalTaxesInput =
      this.monthConsumptionF1Input = this.monthConsumptionF2Input = this.monthConsumptionF3Input =
      this.energyChargePriceInput = this.reactiveEnergyCostsInput = this.tmsChargesInput =
      this.dispatchCostsInput = this.fixedQuotaInput = this.spreadInput = '';

    this.totalConsumptionInputError = this.invoiceAmountInputError = this.totalTaxesInputError =
      this.monthConsumptionF1InputError = this.monthConsumptionF2InputError = this.monthConsumptionF3InputError =
      this.energyChargePriceInputError = this.reactiveEnergyCostsInputError = this.tmsChargesInputError =
      this.dispatchCostsInputError = this.fixedQuotaInputError = this.spreadInputError = null;

    this.dialogErrorMessage = null;
    this.confirmClicked = false;
    this.saveClicked = false;
  }


  loadLocationsData() {
    this.locationService.getLocations().subscribe({
      next: (response) => {
        if (response) {
          this.listOfLocations = response;
          this.locationsDropdownOptions = [];

          for (let i = 0; i < response.length; i++) {
            const location = response[i];
            const locationString = `${location.street ?? ''} ${location.number ?? ''}, ${location.postal_code ?? ''} ${location.city ?? ''}`;
            this.locationsDropdownOptions.push(locationString);
          }
          this.selectedLocationDropdown = undefined;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
      },
      complete: () => {
      }
    });
  }

  validateInvoiceInputs(): boolean {
    this.dialogErrorMessage = null;
    const totalConsumptionValue = this.totalConsumptionInput.trim();
    const invoiceAmountValue = this.invoiceAmountInput.trim();
    const totalTaxesValue = this.totalTaxesInput.trim();

    const monthF1Value = this.monthConsumptionF1Input.trim();
    const monthF2Value = this.monthConsumptionF2Input.trim();
    const monthF3Value = this.monthConsumptionF3Input.trim();

    const energyChargePriceValue = this.energyChargePriceInput.trim();
    const reactiveEnergyCostsValue = this.reactiveEnergyCostsInput.trim();
    const tmsChargesValue = this.tmsChargesInput.trim();
    const dispatchCostsValue = this.dispatchCostsInput.trim();

    const fixedQuotaValue = this.fixedQuotaInput.trim();
    const spreadValue = this.spreadInput.trim();

    let dropdownError = false;
    if (this.selectedLocationDropdown === undefined || this.selectedLocationDropdown === null) {
      this.dialogErrorMessage = this.translocoService.translate('dashboard.select_location');
      dropdownError = true;
    }

    if (this.selectedEnergyTypeDropdown === undefined || this.selectedEnergyTypeDropdown === null) {
      this.dialogErrorMessage = this.translocoService.translate('dashboard.select_energy_type');
      dropdownError = true;
    }

    // Convert date if selected
    const forPeriodDateStr = this.forPeriodDate
      ? this.dateConversion.isoToFormattedDate(this.forPeriodDate.toISOString())
      : '';
    var forPeriodDateError = this.commonValidator.validateDate(forPeriodDateStr, true);

    // Date Validation 
    if (!forPeriodDateError) {
      const today = new Date();
      if (this.forPeriodDate! > today) {
        forPeriodDateError = this.translocoService.translate('dashboard.future_date');
      }
    }

    // Validate fields
    const totalConsumptionError = this.commonValidator.validateNumber(totalConsumptionValue, 0, Number.MAX_SAFE_INTEGER, false, true);
    const invoiceAmountError = this.commonValidator.validateNumber(invoiceAmountValue, 0, Number.MAX_SAFE_INTEGER, false, true);
    const totalTaxesError = this.commonValidator.validateNumber(totalTaxesValue, 0, Number.MAX_SAFE_INTEGER, false, true);

    const f1Error = this.commonValidator.validateNumber(monthF1Value, 0, Number.MAX_SAFE_INTEGER, false, (this.selectedEnergyTypeDropdown == 0));
    const f2Error = this.commonValidator.validateNumber(monthF2Value, 0, Number.MAX_SAFE_INTEGER, false, (this.selectedEnergyTypeDropdown == 0));
    const f3Error = this.commonValidator.validateNumber(monthF3Value, 0, Number.MAX_SAFE_INTEGER, false, (this.selectedEnergyTypeDropdown == 0));

    const energyChargePriceError = this.commonValidator.validateNumber(energyChargePriceValue, 0, Number.MAX_SAFE_INTEGER, false, (this.selectedEnergyTypeDropdown == 0));
    const reactiveEnergyCostsError = this.commonValidator.validateNumber(reactiveEnergyCostsValue, 0, Number.MAX_SAFE_INTEGER, false, (this.selectedEnergyTypeDropdown == 0));
    const tmsChargesError = this.commonValidator.validateNumber(tmsChargesValue, 0, Number.MAX_SAFE_INTEGER, false, true);
    const dispatchCostsError = this.commonValidator.validateNumber(dispatchCostsValue, 0, Number.MAX_SAFE_INTEGER, false, (this.selectedEnergyTypeDropdown == 0));

    const fixedQuotaError = this.commonValidator.validateNumber(fixedQuotaValue, 0, Number.MAX_SAFE_INTEGER, false, (this.selectedEnergyTypeDropdown == 1));
    const spreadError = this.commonValidator.validateNumber(spreadValue, 0, Number.MAX_SAFE_INTEGER, false, (this.selectedEnergyTypeDropdown == 1));

    // Set errors
    this.forPeriodInputError = forPeriodDateError;
    this.totalConsumptionInputError = totalConsumptionError;
    this.invoiceAmountInputError = invoiceAmountError;
    this.totalTaxesInputError = totalTaxesError;

    this.monthConsumptionF1InputError = f1Error;
    this.monthConsumptionF2InputError = f2Error;
    this.monthConsumptionF3InputError = f3Error;

    this.energyChargePriceInputError = energyChargePriceError;
    this.reactiveEnergyCostsInputError = reactiveEnergyCostsError;
    this.tmsChargesInputError = tmsChargesError;
    this.dispatchCostsInputError = dispatchCostsError;

    this.fixedQuotaInputError = fixedQuotaError;
    this.spreadInputError = spreadError;

    // Final check for any errors
    const hasError = !!(
      forPeriodDateError ||
      totalConsumptionError || invoiceAmountError || totalTaxesError ||
      f1Error || f2Error || f3Error ||
      energyChargePriceError || reactiveEnergyCostsError ||
      tmsChargesError || dispatchCostsError ||
      fixedQuotaError || spreadError || this.dialogErrorMessage
    );

    return !hasError;
  }

  populateElectricityHistorical(): HistoricalDataElectricity {
    const electricityData: HistoricalDataElectricity = {
      for_period: this.dateConversion.isoToSimpleDate(this.forPeriodDate!.toISOString()) ?? undefined,
      total_consumption: parseFloat(this.totalConsumptionInput.trim()) || undefined,
      invoice_amount: parseFloat(this.invoiceAmountInput.trim()) || undefined,
      total_taxes: parseFloat(this.totalTaxesInput.trim()) || undefined,

      month_consumption_f1: parseFloat(this.monthConsumptionF1Input.trim()) || undefined,
      month_consumption_f2: parseFloat(this.monthConsumptionF2Input.trim()) || undefined,
      month_consumption_f3: parseFloat(this.monthConsumptionF3Input.trim()) || undefined,

      energy_charge_price: parseFloat(this.energyChargePriceInput.trim()) || undefined,
      reactive_energy_costs: parseFloat(this.reactiveEnergyCostsInput.trim()) || undefined,
      tms_charges: parseFloat(this.tmsChargesInput.trim()) || undefined,
      dispatch_costs: parseFloat(this.dispatchCostsInput.trim()) || undefined
    };

    return electricityData;
  }

  populateGasHistorical(): HistoricalDataGas {
    const gasData: HistoricalDataGas = {
      for_period: this.dateConversion.isoToSimpleDate(this.forPeriodDate!.toISOString()) ?? undefined,
      total_consumption: parseFloat(this.totalConsumptionInput.trim()) || undefined,
      invoice_amount: parseFloat(this.invoiceAmountInput.trim()) || undefined,
      total_taxes: parseFloat(this.totalTaxesInput.trim()) || undefined,

      fixed_quota: parseFloat(this.fixedQuotaInput.trim()) || undefined,
      spread: parseFloat(this.spreadInput.trim()) || undefined,
      tms_charges: parseFloat(this.tmsChargesInput.trim()) || undefined
    };

    return gasData;
  }

  async confirmButtonClick() {
    if (this.confirmClicked) {
      return;
    }
    this.confirmClicked = true;

    if (!this.validateInvoiceInputs()) {
      this.confirmClicked = false;
      return;
    }

    try {
      if (this.selectedEnergyTypeDropdown === 0) {
        const electricityData = this.populateElectricityHistorical();

        await firstValueFrom(this.invoiceService.updateHistoricalElectricityData(this.listOfLocations[this.selectedLocationDropdown!].id, electricityData));

      } else if (this.selectedEnergyTypeDropdown === 1) {
        const gasData = this.populateGasHistorical();

        await firstValueFrom(this.invoiceService.updateHistoricalGasData(this.listOfLocations[this.selectedLocationDropdown!].id, gasData));
      }
      this.dialogErrorMessage = null;

    } catch (error) {
      let crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.dialogErrorMessage = `${await this.apiError.getErrorHeader()} ${await this.apiError.getErrorMessage(crid)}`;
      this.confirmClicked = false;
      return;
    }

    this.confirmClicked = false;
    this.closeModal();
    window.location.reload();
  }

}
